/* eslint-disable max-lines-per-function */
import { FeatureRollout } from '@ms/yammer-data/dist/state/ecsFeatureRollouts/types';
import { Experiment } from '@ms/yammer-data/dist/state/types';
import { useExperimentTreatmentWithoutReporting } from '@ms/yammer-hooks/dist/experiments';
import { useIsFeatureRolloutEnabled } from '@ms/yammer-hooks/dist/featureRollouts';
import { useIsNewGroupFilesEnabled } from '@ms/yammer-hooks/dist/groups';
import { useShowLeaderSettings } from '@ms/yammer-hooks/dist/leaderSettings';
import RedirectToFeed from '@ms/yammer-web-components/dist/route/RedirectToFeed';
import {
  LegacyCreateGroup,
  LegacyDiscoverGroups,
  LegacyGroupDelete,
  LegacyGroupFiles,
  LegacyGroupInfo,
  LegacyGroupInsights,
  LegacyGroupSettings,
  LegacyNetworkAdminSettings,
  LegacyUserSettings,
} from '@ms/yammer-web-components/dist/route/legacyRoutes';
import {
  searchPathFiles,
  searchPathGroups,
  searchPathInbox,
  searchPathThreads,
  searchPathTopics,
  searchPathUsers,
} from '@ms/yammer-web-support/dist/urls';
import React, { FC } from 'react';
import { Route, Switch } from 'react-router-dom';

import {
  Campaign,
  CampaignsDashboard,
  DelegationSettings,
  DiscoverGroups,
  Group,
  Home,
  Inbox,
  InsightsLeaders,
  InsightsQnA,
  LeaderSettings,
  LeadershipCorner,
  LegacyRouteConverter,
  LegacyRouter,
  Questions,
  Search,
  StorylineHome,
  StorylineRouter,
  Thread,
  Topic,
  TopicNetworkQuestion,
  UsagePolicy,
  User,
} from './loadableComponents';

export const ContentRoutes: FC = () => {
  const isNewGroupFilesEnabled = useIsNewGroupFilesEnabled();
  const isLeadershipCornerEnabled = useExperimentTreatmentWithoutReporting(Experiment.VivaEngageLC) === true;
  const leadershipCorner = isLeadershipCornerEnabled ? (
    <Route exact={true} path="/leadershipcorner" component={LeadershipCorner} />
  ) : null;
  const isQuestionsPageEnabled = useExperimentTreatmentWithoutReporting(Experiment.VivaEngageQnA) === true;
  const isStorylineHomeEnabled = useIsFeatureRolloutEnabled(FeatureRollout.StorylineHome);
  const isInsightsQnAPageEnabled = useExperimentTreatmentWithoutReporting(Experiment.VivaInsightsQnA) === true;
  const isInsightsLeadersPageEnabled = useExperimentTreatmentWithoutReporting(Experiment.VivaInsightsLeaders) === true;
  const areCampaignsEnabled = useExperimentTreatmentWithoutReporting(Experiment.Campaigns) === true;
  const showLeaderSettings = useShowLeaderSettings();

  const legacyGroupFilesPage = !isNewGroupFilesEnabled ? (
    <Route exact={true} path="/groups/:groupId/files" component={LegacyGroupFiles} />
  ) : null;

  const questionsPage = isQuestionsPageEnabled
    ? [
        <Route key="Questions" exact={true} path="/questions" component={Questions} />,
        <Route
          key="TopicNetworkQuestionFeed"
          exact={true}
          path="/topics-network-question/:topicId"
          component={TopicNetworkQuestion}
        />,
        <Route
          key="TopicNetworkQuestionFeedSubroute"
          exact={true}
          path="/topics-network-question/:topicId/:topicFeedSubRoute"
          component={TopicNetworkQuestion}
        />,
      ]
    : null;

  const storylineHome = isStorylineHomeEnabled ? (
    <Route exact={true} path="/storylines" component={StorylineHome} />
  ) : null;

  const campaignsPages = areCampaignsEnabled
    ? [
        <Route key="CampaignsDashboard" exact={true} path="/campaigns-dashboard" component={CampaignsDashboard} />,
        <Route
          key="CampaignsDashboardSubRoute"
          exact={true}
          path="/campaigns-dashboard/:campaignsDashboardSubRoute"
          component={CampaignsDashboard}
        />,
      ]
    : null;

  const insightsQnAPages = isInsightsQnAPageEnabled
    ? [
        <Route key="InsightsQnA" exact={true} path="/insights/qna" component={InsightsQnA} />,
        <Route
          key="InsightsQnASubroute"
          exact={true}
          path="/insights/qna/:insightsQnASubroute"
          component={InsightsQnA}
        />,
      ]
    : null;

  const insightsLeadersPages = isInsightsLeadersPageEnabled
    ? [
        <Route key="InsightsLeaders" exact={true} path="/insights/leaders" component={InsightsLeaders} />,
        <Route
          key="InsightsLeadersSubroute"
          exact={true}
          path="/insights/leaders/:insightsLeadersSubroute"
          component={InsightsLeaders}
        />,
      ]
    : null;

  const campaignPage = areCampaignsEnabled ? (
    <Route exact={true} path="/campaigns/:campaignId" component={Campaign} />
  ) : null;

  const leaderSettingPages = showLeaderSettings
    ? [
        <Route key="UserSettingsLeaders" exact={true} path="/user-settings/leaders" component={LeaderSettings} />,
        <Route
          key="UserSettingsLeadersSubroute"
          exact={true}
          path="/user-settings/leaders/:leadersSubroute"
          component={LeaderSettings}
        />,
      ]
    : null;

  return (
    <Switch>
      <Route exact={true} path="/feed" component={Home} />
      {storylineHome}
      <Route exact={true} path="/inbox" component={Inbox} />
      <Route exact={true} path="/inbox/:inboxFeedSubRoute" component={Inbox} />
      {campaignsPages}
      <Route exact={true} path="/create-group" component={LegacyCreateGroup} />
      <Route exact={true} path="/groups/:groupId" component={Group} />
      <Route exact={true} path="/groups/:groupId/settings" component={LegacyGroupSettings} />
      <Route exact={true} path="/groups/:groupId/delete" component={LegacyGroupDelete} />
      <Route exact={true} path="/groups/:groupId/info" component={LegacyGroupInfo} />
      <Route exact={true} path="/groups/:groupId/insights" component={LegacyGroupInsights} />
      {legacyGroupFilesPage}
      <Route exact={true} path="/groups/:groupId/:groupFeedSubRoute" component={Group} />
      {campaignPage}
      <Route exact={true} path="/threads/:threadId/:topLevelMessageId?/:secondLevelMessageId?" component={Thread} />
      <Route exact={true} path="/users/:userId" component={User} />
      <Route exact={true} path="/users/:userId/:userFeedSubRoute" component={User} />
      <Route exact={true} path="/topics/:topicId" component={Topic} />
      <Route exact={true} path="/topics/:topicId/:topicFeedSubRoute" component={Topic} />
      <Route exact={true} path="/usage-policy" component={UsagePolicy} />
      <Route exact={true} path="/user-settings" component={LegacyUserSettings} />
      <Route exact={true} path="/user-settings/delegates" component={DelegationSettings} />
      {leaderSettingPages}
      <Route exact={true} path="/network-admin" component={LegacyNetworkAdminSettings} />
      <Route exact={true} path="/discover-groups" component={DiscoverGroups} />
      <Route exact={true} path="/discover-groups/:discoverGroupsSubRoute" component={DiscoverGroups} />
      <Route exact={true} path={searchPathThreads} component={Search} />
      <Route exact={true} path={searchPathGroups} component={Search} />
      <Route exact={true} path={searchPathUsers} component={Search} />
      <Route exact={true} path={searchPathFiles} component={Search} />
      <Route exact={true} path={searchPathTopics} component={Search} />
      <Route exact={true} path={searchPathInbox} component={Search} />
      {leadershipCorner}
      {questionsPage}
      {insightsQnAPages}
      {insightsLeadersPages}
      <Route exact={true} path="/:networkPermaLink/legacy/groups/:groupId/info" component={LegacyGroupInfo} />
      <Route exact={true} path="/:networkPermaLink/legacy/create-group" component={LegacyCreateGroup} />
      <Route exact={true} path="/:networkPermaLink/legacy/groups" component={LegacyDiscoverGroups} />
      <Route exact={true} path="/:networkPermaLink/users/:userId/storyline" component={StorylineRouter} />
      <Route exact={true} path="/:networkPermaLink/:entityType/:entityId/legacy/*" component={LegacyRouter} />
      <Route exact={true} path="/:networkPermaLink/legacy/:entityType/:entityId" component={LegacyRouter} />
      <Route exact={true} path="/:networkPermaLink/legacy/:entityType" component={LegacyRouter} />
      <Route exact={true} path="/:networkPermaLink/legacy/:entityType/show" component={LegacyRouter} />
      <Route exact={true} path="/:networkPermaLink/legacy/:entityType/inGroup" component={LegacyRouter} />
      <Route exact={true} path="/" component={RedirectToFeed} />
      <Route exact={false} path="*" component={LegacyRouteConverter} />
    </Switch>
  );
};

export default ContentRoutes;
